import { ComponentType } from '@angular/cdk/portal';
import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveEnd, Router } from '@angular/router';
import {
  ApplicationInsights,
  IExceptionTelemetry,
  IPageViewTelemetry
} from '@microsoft/applicationinsights-web';
import { filter } from 'rxjs';

import { ENVIRONMENT } from '@env/environment';

@Injectable()
export class ApplicationInsightsService {
  readonly #router = inject(Router);

  readonly #appInsights = new ApplicationInsights({
    config: {
      connectionString: ENVIRONMENT.appInsights.connectionString,
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      excludeRequestFromAutoTrackingPatterns: [/assets/],
      correlationHeaderExcludedDomains: ['*.clarity.ms']
    }
  });

  readonly excludedDomains = ['clarity.ms'];

  constructor() {
    if (!ENVIRONMENT.appInsights.enabled) {
      return;
    }

    this.#appInsights.loadAppInsights();

    this.#appInsights.appInsights.addTelemetryInitializer(telemetryItem => {
      // Only process dependency telemetry (external requests)
      if (telemetryItem.baseType === 'RemoteDependencyData') {
        const target = (telemetryItem.baseData as { target?: string })?.target;

        if (
          target &&
          this.excludedDomains.some(domain => target.includes(domain))
        ) {
          return false; // Skip logging this item
        }
      }

      telemetryItem.tags['ai.cloud.role'] = ENVIRONMENT.moduleName;

      return true;
    });

    this.#router.events
      .pipe(filter((event): event is ResolveEnd => event instanceof ResolveEnd))
      .subscribe((event: ResolveEnd) => {
        const activatedComponent = this.getActivatedComponent(
          event.state.root
        ) as ComponentType<unknown>;

        if (!activatedComponent) {
          return;
        }

        this.logPageView(
          `${activatedComponent.name} ${this.getRouteTemplate(
            event.state.root
          )}`,
          event.urlAfterRedirects
        );
      });
  }

  setUserId(userId: string): void {
    this.#appInsights.setAuthenticatedUserContext(userId);
  }

  logPageView(name?: string, uri?: string): void {
    const pageView: IPageViewTelemetry = {
      name,
      uri
    };

    this.#appInsights.trackPageView(pageView);
  }

  logException(error: Error, severityLevel?: number): void {
    if (!ENVIRONMENT.appInsights.enabled) {
      return;
    }

    const exception: IExceptionTelemetry = {
      exception: error,
      severityLevel
    };

    this.#appInsights.trackException(exception);
  }

  //#region Private methods

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): unknown {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }

    return snapshot.component;
  }

  private getRouteTemplate(snapshot: ActivatedRouteSnapshot): string {
    let path = '';

    if (snapshot.routeConfig) {
      path += snapshot.routeConfig.path;
    }

    if (snapshot.firstChild) {
      return path + this.getRouteTemplate(snapshot.firstChild);
    }

    return path;
  }

  //#endregion
}
